// src/pages/PreAuthTracking.js
// Pre-Authorization Tracking — kanban board for pre-auth lifecycle

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts';
import * as awsApi from '../services/awsApiService';
import * as apiService from '../services/apiService';
import { demoPreAuths } from '../data/demoData';
import {
  Plus, RefreshCw, AlertTriangle, CheckCircle, Clock,
  XCircle, FileText, X, ChevronRight, Calendar, User, Building2
} from 'lucide-react';

const STATUSES = ['Pending', 'Submitted', 'Approved', 'Denied', 'Expired'];

const STATUS_STYLES = {
  Pending:   { bg: 'bg-slate-50',  border: 'border-slate-300', badge: 'bg-slate-100 text-slate-700',  icon: Clock,       iconColor: 'text-slate-500'  },
  Submitted: { bg: 'bg-blue-50',   border: 'border-blue-300',  badge: 'bg-blue-100 text-blue-700',    icon: FileText,    iconColor: 'text-blue-500'   },
  Approved:  { bg: 'bg-green-50',  border: 'border-green-300', badge: 'bg-green-100 text-green-700',  icon: CheckCircle, iconColor: 'text-green-500'  },
  Denied:    { bg: 'bg-red-50',    border: 'border-red-300',   badge: 'bg-red-100 text-red-700',      icon: XCircle,     iconColor: 'text-red-500'    },
  Expired:   { bg: 'bg-amber-50',  border: 'border-amber-300', badge: 'bg-amber-100 text-amber-700',  icon: AlertTriangle, iconColor: 'text-amber-500' },
};

const PRIORITY_STYLES = {
  Urgent: 'bg-red-100 text-red-700',
  High:   'bg-orange-100 text-orange-700',
  Normal: 'bg-slate-100 text-slate-600',
  Low:    'bg-blue-100 text-blue-600',
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : null;
const formatCurrency = (v) => v != null ? `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 0 })}` : null;

export default function PreAuthTracking() {
  const navigate = useNavigate();
  const { practice } = useAuth();
  const [preauths, setPreauths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedPA, setSelectedPA] = useState(null);
  const [toast, setToast] = useState(null);
  const [patients, setPatients] = useState([]);
  const [submittingId, setSubmittingId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const [form, setForm] = useState({
    patient_name: '', payer_name: '', procedure_codes: '', estimated_amount: '',
    priority: 'Normal', notes: '', submitted_date: new Date().toISOString().split('T')[0]
  });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    if (apiService.getAppMode() === 'demo') {
      setPreauths(demoPreAuths);
      setLoading(false);
      return;
    }
    const [paRes, ptRes] = await Promise.all([
      awsApi.getPreAuths(),
      awsApi.getPatients(practice?.practice_id, { limit: 200 })
    ]);
    if (paRes.success) setPreauths(paRes.data);
    if (ptRes.success) setPatients(ptRes.data || []);
    setLoading(false);
  }, [practice]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.patient_name || !form.payer_name) return showToast('Patient name and payer required', 'error');
    const codes = form.procedure_codes.split(',').map(s => s.trim()).filter(Boolean);
    const res = await awsApi.createPreAuth({ ...form, procedure_codes: codes });
    if (res.success) {
      showToast('Pre-auth created');
      setShowNewForm(false);
      setForm({ patient_name: '', payer_name: '', procedure_codes: '', estimated_amount: '', priority: 'Normal', notes: '', submitted_date: new Date().toISOString().split('T')[0] });
      load();
    } else {
      showToast(res.error, 'error');
    }
  };

  const handleUpdate = async (id, updates) => {
    const res = await awsApi.updatePreAuth(id, updates);
    if (res.success) {
      showToast('Pre-auth updated');
      setSelectedPA(res.data);
      load();
    } else {
      showToast(res.error, 'error');
    }
  };

  const handleSubmitEDI = async (id) => {
    setSubmittingId(id);
    const res = await awsApi.submitPreAuth(id);
    setSubmittingId(null);
    if (res.success) {
      showToast(`Submitted to clearinghouse — trace #${res.data?.traceNum || ''}`);
      load();
      setSelectedPA(null);
    } else {
      showToast(res.error || 'Submission failed', 'error');
    }
  };

  const handleSyncResponses = async () => {
    setSyncing(true);
    const res = await awsApi.syncPreAuthResponses();
    setSyncing(false);
    if (res.success) {
      const d = res.data;
      showToast(`Sync complete — ${d.updated || 0} updated (${d.approved || 0} approved, ${d.denied || 0} denied)`);
      load();
    } else {
      showToast(res.error || 'Sync failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    const res = await awsApi.deletePreAuth(id);
    if (res.success) {
      showToast('Pre-auth deleted');
      setSelectedPA(null);
      load();
    } else {
      showToast(res.error, 'error');
    }
  };

  const byStatus = STATUSES.reduce((acc, s) => {
    acc[s] = preauths.filter(p => p.status === s);
    return acc;
  }, {});

  return (
    <div data-tour="preauth-content" className="min-h-screen bg-slate-50">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2
          ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
          {toast.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText size={18} className="text-purple-700" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Pre-Auth Tracking</h1>
              <p className="text-xs text-slate-500">Manage pre-authorization requests and approvals</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleSyncResponses}
            disabled={syncing}
            className="flex items-center gap-2 bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync Responses'}
          </button>
          <button onClick={() => setShowNewForm(true)}
            className="flex items-center gap-2 bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-800">
            <Plus size={16} /> New Pre-Auth
          </button>
        </div>
      </div>

      {/* New Form */}
      {showNewForm && (
        <div className="bg-white border-b border-slate-200 px-6 py-5">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-800">New Pre-Authorization Request</h2>
              <button onClick={() => setShowNewForm(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Patient Name *</label>
                <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.patient_name} onChange={e => setForm(f => ({ ...f, patient_name: e.target.value }))}
                  list="patient-list" placeholder="Patient name" />
                <datalist id="patient-list">
                  {patients.map(p => <option key={p.id} value={`${p.first_name} ${p.last_name}`} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Payer / Insurance *</label>
                <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.payer_name} onChange={e => setForm(f => ({ ...f, payer_name: e.target.value }))}
                  placeholder="e.g. Delta Dental" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Procedure Codes</label>
                <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.procedure_codes} onChange={e => setForm(f => ({ ...f, procedure_codes: e.target.value }))}
                  placeholder="D2740, D2750 (comma separated)" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Estimated Amount</label>
                <input type="number" step="0.01" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.estimated_amount} onChange={e => setForm(f => ({ ...f, estimated_amount: e.target.value }))}
                  placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Priority</label>
                <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option>Urgent</option><option>High</option><option>Normal</option><option>Low</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Submitted Date</label>
                <input type="date" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.submitted_date} onChange={e => setForm(f => ({ ...f, submitted_date: e.target.value }))} />
              </div>
              <div className="col-span-3">
                <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                <input className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Additional notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowNewForm(false)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreate}
                className="px-4 py-2 text-sm bg-purple-700 text-white rounded-lg hover:bg-purple-800 font-medium">Create Pre-Auth</button>
            </div>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div className="p-6 overflow-x-auto">
        {loading ? (
          <div className="text-center py-16 text-slate-500">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-purple-600" />Loading pre-auths...
          </div>
        ) : (
          <div className="flex gap-4 min-w-max">
            {STATUSES.map(status => {
              const style = STATUS_STYLES[status];
              const Icon = style.icon;
              const items = byStatus[status] || [];
              return (
                <div key={status} className="w-72">
                  <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-t border-l border-r ${style.border} ${style.bg}`}>
                    <div className="flex items-center gap-2">
                      <Icon size={14} className={style.iconColor} />
                      <span className="text-sm font-semibold text-slate-700">{status}</span>
                    </div>
                    <span className="text-xs bg-white border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">{items.length}</span>
                  </div>
                  <div className={`min-h-48 space-y-2 p-2 rounded-b-lg border ${style.border} ${style.bg}`}>
                    {items.map(pa => (
                      <div key={pa.id}
                        data-tour={pa.id === 'pa-demo-000' ? 'sarah-preauth-row' : undefined}
                        className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => setSelectedPA(pa)}>
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-semibold text-slate-800 leading-tight">{pa.patient_name}</p>
                          {pa.priority !== 'Normal' && (
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${PRIORITY_STYLES[pa.priority]}`}>{pa.priority}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                          <Building2 size={11} />{pa.payer_name}
                        </div>
                        {pa.procedure_codes?.length > 0 && (
                          <div className="text-xs text-slate-500 mb-1">
                            {(pa.procedure_codes || []).slice(0, 3).join(', ')}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          {pa.estimated_amount && <span className="text-xs font-medium text-teal-700">{formatCurrency(pa.estimated_amount)}</span>}
                          {pa.submitted_date && (
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Calendar size={10} />{formatDate(pa.submitted_date)}
                            </span>
                          )}
                        </div>
                        {pa.auth_number && (
                          <div className="mt-2 text-xs bg-green-50 text-green-700 px-2 py-1 rounded">Auth #{pa.auth_number}</div>
                        )}
                      </div>
                    ))}
                    {items.length === 0 && (
                      <div className="text-center py-6 text-xs text-slate-400">No {status.toLowerCase()} pre-auths</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedPA && (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/30" onClick={() => setSelectedPA(null)} />
          <div
            data-tour={selectedPA?.id === 'pa-demo-000' ? 'sarah-preauth-detail' : undefined}
            className="w-96 bg-white shadow-2xl overflow-y-auto">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Pre-Auth Detail</h2>
              <button onClick={() => setSelectedPA(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Patient</p>
                <p className="font-semibold text-slate-800 flex items-center gap-2"><User size={14} />{selectedPA.patient_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Payer</p>
                <p className="font-medium text-slate-700 flex items-center gap-2"><Building2 size={14} />{selectedPA.payer_name}</p>
              </div>
              {selectedPA.procedure_codes?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Procedure Codes</p>
                  <div className="flex flex-wrap gap-1">
                    {(selectedPA.procedure_codes || []).map(code => (
                      <span key={code} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">{code}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selectedPA.estimated_amount && (
                  <div><p className="text-xs text-slate-500">Estimated</p><p className="font-medium">{formatCurrency(selectedPA.estimated_amount)}</p></div>
                )}
                {selectedPA.approved_amount && (
                  <div><p className="text-xs text-slate-500">Approved</p><p className="font-medium text-green-700">{formatCurrency(selectedPA.approved_amount)}</p></div>
                )}
                {selectedPA.submitted_date && (
                  <div><p className="text-xs text-slate-500">Submitted</p><p className="font-medium">{formatDate(selectedPA.submitted_date)}</p></div>
                )}
                {selectedPA.expiration_date && (
                  <div><p className="text-xs text-slate-500">Expires</p><p className="font-medium">{formatDate(selectedPA.expiration_date)}</p></div>
                )}
              </div>
              {selectedPA.auth_number && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
                  <p className="text-xs text-green-600 font-medium">Auth Number</p>
                  <p className="text-green-800 font-semibold">{selectedPA.auth_number}</p>
                </div>
              )}
              {selectedPA.denial_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
                  <p className="text-xs text-red-600 font-medium">Denial Reason</p>
                  <p className="text-red-700">{selectedPA.denial_reason}</p>
                </div>
              )}
              {selectedPA.notes && (
                <div><p className="text-xs text-slate-500 mb-1">Notes</p><p className="text-sm text-slate-700">{selectedPA.notes}</p></div>
              )}

              {/* Submit to Payer via EDI 278 */}
              {(selectedPA.status === 'Pending') && (
                <div className="border border-purple-200 rounded-lg p-3 bg-purple-50">
                  <p className="text-xs font-semibold text-purple-700 mb-2">Electronic Submission</p>
                  <p className="text-xs text-purple-600 mb-3">Send this pre-auth request to the clearinghouse via EDI 278. The payer will respond and FlowRCM will auto-update the status.</p>
                  <button
                    onClick={() => handleSubmitEDI(selectedPA.id)}
                    disabled={submittingId === selectedPA.id}
                    className="w-full flex items-center justify-center gap-2 bg-purple-700 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-purple-800 disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={submittingId === selectedPA.id ? 'animate-spin' : ''} />
                    {submittingId === selectedPA.id ? 'Submitting...' : 'Submit to Payer'}
                  </button>
                </div>
              )}

              {selectedPA.edi_trace_number && (
                <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-600">
                  <span className="font-medium">Trace #:</span> {selectedPA.edi_trace_number}
                </div>
              )}

              {/* Update Status */}
              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">Update Status</p>
                <div className="grid grid-cols-2 gap-2">
                  {['Submitted', 'Approved', 'Denied', 'Expired'].filter(s => s !== selectedPA.status).map(s => (
                    <button key={s}
                      onClick={() => handleUpdate(selectedPA.id, { status: s })}
                      className={`text-xs py-2 px-3 rounded-lg border font-medium transition-colors ${
                        s === 'Approved' ? 'border-green-300 text-green-700 hover:bg-green-50' :
                        s === 'Denied' ? 'border-red-300 text-red-700 hover:bg-red-50' :
                        'border-slate-300 text-slate-600 hover:bg-slate-50'
                      }`}>
                      Mark {s}
                    </button>
                  ))}
                </div>
                {selectedPA.status === 'Approved' && !selectedPA.auth_number && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Auth Number</label>
                    <div className="flex gap-2">
                      <input id="auth-num-input" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Enter auth number" />
                      <button onClick={() => {
                        const val = document.getElementById('auth-num-input').value;
                        if (val) handleUpdate(selectedPA.id, { auth_number: val });
                      }} className="px-3 py-2 bg-green-700 text-white text-xs rounded-lg hover:bg-green-800">Save</button>
                    </div>
                  </div>
                )}
                {selectedPA.status === 'Denied' && !selectedPA.denial_reason && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Denial Reason</label>
                    <div className="flex gap-2">
                      <input id="denial-input" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Enter denial reason" />
                      <button onClick={() => {
                        const val = document.getElementById('denial-input').value;
                        if (val) handleUpdate(selectedPA.id, { denial_reason: val });
                      }} className="px-3 py-2 bg-red-700 text-white text-xs rounded-lg hover:bg-red-800">Save</button>
                    </div>
                  </div>
                )}
              </div>

              <button onClick={() => handleDelete(selectedPA.id)}
                className="w-full text-xs text-red-600 hover:text-red-700 py-2 border border-red-200 rounded-lg hover:bg-red-50 mt-2">
                Delete Pre-Auth
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
